import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { HelmetProvider } from 'react-helmet-async';
import HomePage from './components/home/HomePage';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import OpinionDetail from './components/opinions/OpinionDetail';
import OpinionBrochure from './components/opinions/OpinionBrochure';
import OpinionStats from './components/opinions/OpinionStats';
import CreateOpinion from './components/opinions/CreateOpinion';
import Dashboard from './components/dashboard/Dashboard';
import MyOpinions from './components/profile/MyOpinions';
import ProfileSettings from './components/profile/ProfileSettings';
import UserProfile from './components/profile/UserProfile';
import FollowersPage from './components/profile/FollowersPage';
import SearchPage from './components/search/SearchPage';
import AuthGuard from './components/auth/AuthGuard';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
      light: '#9aa7f5',
      dark: '#4a56d0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#764ba2',
      light: '#a77bc5',
      dark: '#4a2c6a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 16,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.9375rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.875rem',
      lineHeight: 1.4,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.9375rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  shadows: [
    'none',
    '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 3px 6px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.24)',
    '0px 4px 8px rgba(0, 0, 0, 0.12), 0px 3px 6px rgba(0, 0, 0, 0.24)',
    '0px 5px 10px rgba(0, 0, 0, 0.12), 0px 4px 8px rgba(0, 0, 0, 0.24)',
    '0px 6px 12px rgba(0, 0, 0, 0.12), 0px 5px 10px rgba(0, 0, 0, 0.24)',
    '0px 7px 14px rgba(0, 0, 0, 0.12), 0px 6px 12px rgba(0, 0, 0, 0.24)',
    '0px 8px 16px rgba(0, 0, 0, 0.12), 0px 7px 14px rgba(0, 0, 0, 0.24)',
    '0px 9px 18px rgba(0, 0, 0, 0.12), 0px 8px 16px rgba(0, 0, 0, 0.24)',
    '0px 10px 20px rgba(0, 0, 0, 0.12), 0px 9px 18px rgba(0, 0, 0, 0.24)',
    '0px 11px 22px rgba(0, 0, 0, 0.12), 0px 10px 20px rgba(0, 0, 0, 0.24)',
    '0px 12px 24px rgba(0, 0, 0, 0.12), 0px 11px 22px rgba(0, 0, 0, 0.24)',
    '0px 13px 26px rgba(0, 0, 0, 0.12), 0px 12px 24px rgba(0, 0, 0, 0.24)',
    '0px 14px 28px rgba(0, 0, 0, 0.12), 0px 13px 26px rgba(0, 0, 0, 0.24)',
    '0px 15px 30px rgba(0, 0, 0, 0.12), 0px 14px 28px rgba(0, 0, 0, 0.24)',
    '0px 16px 32px rgba(0, 0, 0, 0.12), 0px 15px 30px rgba(0, 0, 0, 0.24)',
    '0px 17px 34px rgba(0, 0, 0, 0.12), 0px 16px 32px rgba(0, 0, 0, 0.24)',
    '0px 18px 36px rgba(0, 0, 0, 0.12), 0px 17px 34px rgba(0, 0, 0, 0.24)',
    '0px 19px 38px rgba(0, 0, 0, 0.12), 0px 18px 36px rgba(0, 0, 0, 0.24)',
    '0px 20px 40px rgba(0, 0, 0, 0.12), 0px 19px 38px rgba(0, 0, 0, 0.24)',
    '0px 21px 42px rgba(0, 0, 0, 0.12), 0px 20px 40px rgba(0, 0, 0, 0.24)',
    '0px 22px 44px rgba(0, 0, 0, 0.12), 0px 21px 42px rgba(0, 0, 0, 0.24)',
    '0px 23px 46px rgba(0, 0, 0, 0.12), 0px 22px 44px rgba(0, 0, 0, 0.24)',
    '0px 24px 48px rgba(0, 0, 0, 0.12), 0px 23px 46px rgba(0, 0, 0, 0.24)',
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12), 0px 3px 6px rgba(0, 0, 0, 0.24)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px !important',
          '&:last-child': {
            paddingBottom: '16px !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '0.9375rem',
          minHeight: 40,
        },
        sizeSmall: {
          padding: '6px 16px',
          minHeight: 32,
          fontSize: '0.875rem',
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12), 0px 3px 6px rgba(0, 0, 0, 0.24)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontSize: '1rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 28,
          fontSize: '0.875rem',
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-body1': {
            fontSize: '1rem',
          },
          '&.MuiTypography-body2': {
            fontSize: '0.9375rem',
          },
          '&.MuiTypography-caption': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
  },
});

function AppRoutes() {
  const { user, setUser } = useAuth();

  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/google/callback" element={<Login />} />
        <Route
          path="/home"
          element={
            <AuthGuard>
              <Dashboard />
            </AuthGuard>
          }
        />
        <Route
          path="/create"
          element={
            <AuthGuard>
              <CreateOpinion />
            </AuthGuard>
          }
        />
        <Route
          path="/my-opinions"
          element={
            <AuthGuard>
              <MyOpinions />
            </AuthGuard>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthGuard>
              <ProfileSettings />
            </AuthGuard>
          }
        />
        <Route path="/opinions/:id" element={<OpinionDetail />} />
        <Route path="/opinions/:id/brochure" element={<OpinionBrochure />} />
        <Route
          path="/opinions/:id/stats"
          element={
            <AuthGuard>
              <OpinionStats />
            </AuthGuard>
          }
        />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/profile/:userId" element={<UserProfile />} />
        <Route path="/profile/:userId/followers" element={<FollowersPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3}>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
